import { siteTexts } from "../../../../shared/i18n/siteTexts";

export type UserRole = "user" | "admin" | "organizer";
export type UserSex = "male" | "female" | "other" | "";
export type UserCategory = "7a" | "6a" | "5a" | "4a" | "3a" | "2a" | "open" | "";
export type UserSidePreference = "direita" | "esquerda" | "ambos" | "";

export const USER_CATEGORY_VALUES: UserCategory[] = [
  "",
  "7a",
  "6a",
  "5a",
  "4a",
  "3a",
  "2a",
  "open",
];

export function normalizeUserSex(value: unknown): UserSex {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (normalized === "male" || normalized === "female" || normalized === "other") {
    return normalized;
  }
  return "";
}

export function normalizeUserCategory(value: unknown): UserCategory {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  if (normalized === "7a" || normalized === "7" || normalized === "iniciante") {
    return "7a";
  }
  if (normalized === "6a" || normalized === "6") return "6a";
  if (normalized === "5a" || normalized === "5") return "5a";
  if (normalized === "4a" || normalized === "4") return "4a";
  if (normalized === "3a" || normalized === "3") return "3a";
  if (normalized === "2a" || normalized === "2") return "2a";
  if (normalized === "open" || normalized === "aberta" || normalized === "aberto") {
    return "open";
  }
  return "";
}

export function normalizeUserSidePreference(value: unknown): UserSidePreference {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  if (["direita", "right", "d"].includes(normalized)) return "direita";
  if (["esquerda", "left", "e"].includes(normalized)) return "esquerda";
  if (["ambos", "both", "any"].includes(normalized)) return "ambos";
  return "";
}

export function userCategoryLabel(value: UserCategory): string {
  if (value === "7a") return siteTexts.common.category.c7a;
  if (value === "6a") return siteTexts.common.category.c6a;
  if (value === "5a") return siteTexts.common.category.c5a;
  if (value === "4a") return siteTexts.common.category.c4a;
  if (value === "3a") return siteTexts.common.category.c3a;
  if (value === "2a") return siteTexts.common.category.c2a;
  if (value === "open") return siteTexts.common.category.open;
  return siteTexts.common.category.notInformed;
}

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  pendingEmail?: string;
  pendingEmailRequestedAt?: string;
  emailChangeRequested?: boolean;
  emailChangeConfirmationLink?: string;
  emailChangeExpiresAt?: string;
  role: UserRole;
  cpf: string;
  phone: string;
  sex: UserSex;
  category: UserCategory;
  clubId?: number | null;
  clubName?: string;
  clubSlug?: string;
  clubLogoUrl?: string;
  avatarUrl?: string;
  createdAt?: string;
  created_at?: string;
  createdDate?: string;
  registrationDate?: string;
  memberSince?: string;
}

export interface AuthPayload {
  token: string;
  user: AuthUser;
}

export type OrganizerRankingMode = "tnp_standard" | "custom";
export type OrganizerRankingPointsTable = {
  super8: {
    first: number | "";
    second: number | "";
    third: number | "";
    fourth: number | "";
    fifth: number | "";
    sixth: number | "";
    seventh: number | "";
    eighth: number | "";
  };
  challenge: {
    first: number | "";
    second: number | "";
    third: number | "";
    participation: number | "";
  };
  poco: {
    first: number | "";
    second: number | "";
    third: number | "";
    participation: number | "";
  };
  tournament: {
    champion: number | "";
    runnerUp: number | "";
    semifinal: number | "";
    quarterfinal: number | "";
    participation: number | "";
  };
};

export interface OrganizerRankingSettings {
  mode: OrganizerRankingMode;
  points: OrganizerRankingPointsTable;
  officialPoints: OrganizerRankingPointsTable;
  hasCustomTable: boolean;
  updatedAt?: string | null;
}

export type OrganizerVisualIdentityMode = "tnp_standard" | "custom";
export type OrganizerVisualIdentityBackgroundMode = "blue" | "black" | "white";

export type OrganizerVisualIdentityColors = {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
};

export interface OrganizerVisualIdentitySettings {
  mode: OrganizerVisualIdentityMode;
  colors: OrganizerVisualIdentityColors;
  defaultColors: OrganizerVisualIdentityColors;
  isCustom: boolean;
  backgroundMode: OrganizerVisualIdentityBackgroundMode;
  displayName?: string | null;
  logoUrl?: string | null;
  updatedAt?: string | null;
}

export interface CreateUserPayload {
  name: string;
  email: string;
  password: string;
  cpf: string;
  phone: string;
  sex: UserSex;
  category: UserCategory;
  clubId?: number | null;
  role: UserRole;
}

export interface UpdateUserPayload {
  name: string;
  email: string;
  role: UserRole;
  cpf: string;
  phone: string;
  sex: UserSex;
  category: UserCategory;
  clubId?: number | null;
  avatarUrl?: string;
  password?: string;
}

export type CategoryChangeRequestStatus = "pending" | "approved" | "rejected";

export interface CategoryChangeRequest {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  currentCategory: UserCategory;
  requestedCategory: UserCategory;
  reason: string;
  status: CategoryChangeRequestStatus;
  adminNote: string;
  resolvedByUserId: number | null;
  resolvedByName: string;
  resolvedAt: string;
  createdAt: string;
  updatedAt: string;
}
